import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { statusBadgeClasses, statusLabel, statusDotClass, formatDate } from "@/lib/statusColor";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { getBoardSession, setBoardSession, clearBoardSession, boardHeaders } from "@/lib/boardSession";
import {
  CheckSquare, Calendar as CalIcon, CalendarIcon, Mail, Send, CheckCircle2, RotateCcw,
  MessageSquare, ListChecks, Clock3, AlertTriangle, CircleCheck, UserPlus, Copy, ArrowRightLeft, Lock, LogOut,
  Plus, Pencil, Trash2, Flag,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicBoard() {
  const { token } = useParams();
  const [phase, setPhase] = useState("checking"); // checking | setup | login | board
  const [assigneeInfo, setAssigneeInfo] = useState(null);
  const [boardData, setBoardData] = useState(null);
  const [author, setAuthor] = useState("");

  const loadStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/public/board/${token}/status`);
      setAssigneeInfo(data.assignee);
      // Attempt to reuse existing session
      if (getBoardSession(token)) {
        try {
          await loadBoard();
          return;
        } catch {
          clearBoardSession(token);
        }
      }
      setPhase(data.password_set ? "login" : "setup");
    } catch (err) {
      if (err?.response?.status === 404) setPhase("notfound");
      else toast.error(formatApiError(err));
    }
  };

  const loadBoard = async () => {
    const { data } = await axios.get(`${API}/public/board/${token}`, { headers: boardHeaders(token) });
    setBoardData(data);
    setAssigneeInfo(data.assignee);
    setAuthor((prev) => prev || data.assignee?.name || "");
    setPhase("board");
  };

  useEffect(() => {
    loadStatus();
     
  }, [token]);

  const onAuthed = async (sessionToken) => {
    setBoardSession(token, sessionToken);
    try {
      await loadBoard();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const logout = () => {
    clearBoardSession(token);
    setPhase("login");
    setBoardData(null);
  };

  if (phase === "checking") {
    return <div className="min-h-screen bg-slate-50 grid place-items-center text-slate-500 text-sm">Loading…</div>;
  }
  if (phase === "notfound") {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center px-6">
        <Card className="p-8 max-w-md text-center rounded-md border-slate-200 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Board not found</h1>
          <p className="text-slate-500 mt-2">This link may have been removed or is invalid.</p>
        </Card>
      </div>
    );
  }
  if (phase === "setup") {
    return <SetupScreen token={token} assignee={assigneeInfo} onDone={onAuthed} />;
  }
  if (phase === "login") {
    return <LoginScreen token={token} assignee={assigneeInfo} onDone={onAuthed} />;
  }
  return <BoardScreen token={token} boardData={boardData} setBoardData={setBoardData} author={author} setAuthor={setAuthor} onLogout={logout} />;
}

function AuthShell({ children, subtitle, assignee }) {
  return (
    <div className="min-h-screen bg-slate-50 grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1518655048521-f130df041f66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxtaW5pbWFsJTIwd29ya3NwYWNlJTIwZGVzayUyMGFic3RyYWN0fGVufDB8fHx8MTc4NDQ3NDI5OXww&ixlib=rb-4.1.0&q=85"
          alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/50" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Your task board</h2>
          <p className="mt-3 text-slate-200 max-w-md leading-relaxed">See your work, update progress, comment, and delegate — securely with your own password.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <Card className="w-full max-w-md rounded-md border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-md bg-slate-900 text-white grid place-items-center"><CheckSquare size={16} /></div>
            <span className="font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>TaskFlow</span>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Welcome, {assignee?.name}
          </h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><Mail size={12} /> {assignee?.email}</p>
          <p className="text-sm text-slate-600 mt-4">{subtitle}</p>
          <div className="mt-5">{children}</div>
        </Card>
      </div>
    </div>
  );
}

function SetupScreen({ token, assignee, onDone }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 4) return toast.error("Password must be at least 4 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/board/${token}/setup`, { password: pw });
      onDone(data.session_token);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };
  return (
    <AuthShell subtitle="Set a password to secure your board. Use this password to log in every time you visit." assignee={assignee}>
      <form onSubmit={submit} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="pw">Create password</Label>
          <Input id="pw" type="password" minLength={4} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="board-setup-pw" autoFocus />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pw2">Confirm password</Label>
          <Input id="pw2" type="password" minLength={4} required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="board-setup-pw2" />
        </div>
        <Button type="submit" disabled={busy} className="w-full" data-testid="board-setup-btn">
          {busy ? "Setting up…" : "Set password & continue"}
        </Button>
        <p className="text-[11px] text-slate-500 text-center">Only you have this link. Anyone with it and your password can view your tasks.</p>
      </form>
    </AuthShell>
  );
}

function LoginScreen({ token, assignee, onDone }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/board/${token}/login`, { password: pw });
      onDone(data.session_token);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <AuthShell subtitle="Enter your board password to see your tasks." assignee={assignee}>
      <form onSubmit={submit} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="pw"><Lock size={11} className="inline mr-1" /> Password</Label>
          <Input id="pw" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="board-login-pw" autoFocus />
        </div>
        <Button type="submit" disabled={busy} className="w-full" data-testid="board-login-btn">
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthShell>
  );
}

function BoardScreen({ token, boardData, setBoardData, author, setAuthor, onLogout }) {
  const assignee = boardData.assignee;
  const tasks = boardData.tasks;
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("tasks");
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const q = query.trim().toLowerCase();
  const matchesQuery = (t) => !q || (
    (t.title || "").toLowerCase().includes(q) ||
    (t.description || "").toLowerCase().includes(q)
  );
  const matchesFilter = (t) => {
    if (filter === "all") return true;
    if (filter === "completed") return t.status === "completed";
    if (filter === "delegated") return t.delegated_by_me === true || t.author_view === true;
    return t.color === filter && t.status !== "completed" && !t.delegated_by_me && !t.author_view;
  };
  const filteredTasks = tasks.filter((t) => matchesQuery(t) && matchesFilter(t));
  const myTasks = filteredTasks.filter((t) => !t.delegated_by_me && !t.author_view);
  const delegated = filteredTasks.filter((t) => t.delegated_by_me || t.author_view);

  const stats = useMemo(() => {
    // Stats computed on ALL tasks (unfiltered) so counts don't shift when filtering
    const myAll = tasks.filter((t) => !t.delegated_by_me && !t.author_view);
    return {
      all: myAll.length,
      green: myAll.filter((t) => t.color === "green" && t.status !== "completed").length,
      yellow: myAll.filter((t) => t.color === "yellow" && t.status !== "completed").length,
      red: myAll.filter((t) => t.color === "red" && t.status !== "completed").length,
      completed: myAll.filter((t) => t.status === "completed").length,
      delegated: tasks.filter((t) => t.delegated_by_me || t.author_view).length,
      unread: tasks.filter((t) => t.unread).length,
    };
  }, [tasks]);

  const toggleStatus = async (task) => {
    if (task.author_view && task.assignee_email !== boardData.assignee?.email) {
      return toast.info("This task is assigned to someone else — only they can mark it complete.");
    }
    const next = task.status === "completed" ? "in_progress" : "completed";
    try {
      const { data } = await axios.patch(`${API}/public/board/${token}/tasks/${task.id}/status`, { status: next }, { headers: boardHeaders(token) });
      setBoardData((prev) => ({ ...prev, tasks: prev.tasks.map((t) => (t.id === task.id ? { ...t, ...data, delegated_by_me: t.delegated_by_me, sub_share_token: t.sub_share_token, authored_by_me: t.authored_by_me, author_view: t.author_view } : t)) }));
      toast.success(next === "completed" ? "Marked as completed" : "Reopened");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const markRead = async (taskId) => {
    try {
      await axios.post(`${API}/public/board/${token}/tasks/${taskId}/read`, {}, { headers: boardHeaders(token) });
      setBoardData((prev) => ({ ...prev, tasks: prev.tasks.map((t) => (t.id === taskId ? { ...t, unread: false } : t)) }));
    } catch {}
  };
  const onSubtaskCreated = (task) => {
    setBoardData((prev) => ({ ...prev, tasks: [task, ...prev.tasks] }));
    toast.success("Delegated");
  };
  const onNewTaskCreated = (task) => {
    setBoardData((prev) => ({ ...prev, tasks: [task, ...prev.tasks] }));
    toast.success("Task created");
  };
  const onTaskUpdated = (task) => {
    setBoardData((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t) => (t.id === task.id ? { ...t, ...task } : t)),
    }));
    toast.success("Task updated");
  };
  const onDeleteAuthoredTask = async (task) => {
    if (!window.confirm(`Delete "${task.title}"? It will be moved to Trash for 24 hours.`)) return;
    try {
      await axios.delete(`${API}/public/board/${token}/tasks/${task.id}`, { headers: boardHeaders(token) });
      setBoardData((prev) => ({ ...prev, tasks: prev.tasks.filter((t) => t.id !== task.id) }));
      toast.success("Task deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openTasks = myTasks.filter((t) => t.status !== "completed");
  const doneTasks = myTasks.filter((t) => t.status === "completed");
  const donePct = stats.all === 0 ? 0 : Math.round((stats.completed / stats.all) * 100);

  const STAT_CHIPS = [
    { key: "all", label: "Total", value: stats.all, cls: "bg-slate-900 text-white" },
    { key: "green", label: "On Track", value: stats.green, cls: "bg-emerald-100 text-emerald-800" },
    { key: "yellow", label: "Past", value: stats.yellow, cls: "bg-amber-100 text-amber-800" },
    { key: "red", label: "Overdue", value: stats.red, cls: "bg-rose-100 text-rose-800" },
    { key: "completed", label: "Done", value: stats.completed, cls: "bg-slate-100 text-slate-800" },
  ];

  const WINDOWS = [
    { key: "green", title: "On Track", header: "bg-gradient-to-br from-emerald-500 to-teal-600",
      tint: "border-l-emerald-500 bg-emerald-50/60", tasks: openTasks.filter((t) => t.color === "green") },
    { key: "yellow", title: "Past Deadline", header: "bg-gradient-to-br from-amber-500 to-orange-500",
      tint: "border-l-amber-500 bg-amber-50/60", tasks: openTasks.filter((t) => t.color === "yellow") },
    { key: "red", title: "Overdue", header: "bg-gradient-to-br from-rose-500 to-red-600",
      tint: "border-l-rose-500 bg-rose-50/60", tasks: openTasks.filter((t) => t.color === "red") },
    { key: "completed", title: "Completed", header: "bg-gradient-to-br from-slate-500 to-slate-700",
      tint: "border-l-slate-400 bg-slate-50", tasks: doneTasks },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-slate-900 text-white grid place-items-center"><CheckSquare size={16} /></div>
          <span className="font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>TaskFlow</span>
          <span className="ml-auto text-xs text-slate-500 hidden sm:inline">Shared board</span>
          <Button size="sm" onClick={() => setNewTaskOpen(true)} data-testid="board-new-task-btn" className="ml-2">
            <Plus size={13} className="mr-1.5" /> New Task
          </Button>
          <Button variant="outline" size="sm" onClick={onLogout} data-testid="board-logout-btn" className="ml-2">
            <LogOut size={13} className="mr-1.5" /> Log out
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <Card className="rounded-md border-slate-200 shadow-sm p-6 mb-4" data-testid="board-header">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-slate-900 text-white grid place-items-center shrink-0 text-base font-semibold">
              {(assignee?.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Tasks for</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="board-assignee-name">
                {assignee?.name}
              </h1>
              <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-1"><Mail size={12} /> {assignee?.email}</p>
            </div>
            {stats.unread > 0 && (
              <Badge className="bg-blue-600 hover:bg-blue-600 text-white gap-1.5" data-testid="board-unread-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />{stats.unread} new
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-6" data-testid="board-stats">
            {STAT_CHIPS.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setFilter((cur) => (cur === s.key && s.key !== "all" ? "all" : s.key))}
                data-testid={`board-stat-${s.key}`}
                aria-pressed={filter === s.key}
                className={`text-left p-2.5 rounded-md border bg-white transition-all hover:border-slate-300 ${filter === s.key ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200"}`}
              >
                <div className={`inline-flex items-center gap-1.5 text-[10px] px-1.5 py-0.5 rounded-md uppercase tracking-wider font-semibold ${s.cls}`}>
                  {s.label}
                </div>
                <div className="text-2xl font-semibold text-slate-900 mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{s.value}</div>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-5">
            <Progress value={donePct} className="h-1.5 flex-1" data-testid="board-progress" />
            <span className="text-xs text-slate-500 tabular-nums shrink-0">{donePct}%</span>
          </div>
        </Card>

        <div className="flex items-center gap-1 mb-4 bg-white rounded-md border border-slate-200 p-1 w-fit" role="tablist" data-testid="board-tabs">
          <button type="button" onClick={() => setTab("tasks")} aria-selected={tab === "tasks"}
            className={`text-sm px-3 py-1.5 rounded transition-colors ${tab === "tasks" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"}`} data-testid="board-tab-tasks">
            Tasks
          </button>
          <button type="button" onClick={() => setTab("todo")} aria-selected={tab === "todo"}
            className={`text-sm px-3 py-1.5 rounded transition-colors ${tab === "todo" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"}`} data-testid="board-tab-todo">
            My To-Do
          </button>
        </div>

        {tab === "todo" ? (
          <BoardTodo token={token} />
        ) : (
        <>
        <div className="relative mb-4">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search your tasks by subject or description…" className="pl-9 pr-10 h-10 bg-white" data-testid="board-search-input" />
          {query && (
            <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-500" aria-label="Clear search" data-testid="board-search-clear">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
            </button>
          )}
        </div>

        {myTasks.length === 0 && delegated.length === 0 ? (
          <Card className="p-10 rounded-md border-slate-200 shadow-sm text-center"><p className="text-slate-500">No tasks {filter === "all" && !q ? "assigned yet" : "match this filter"}.</p></Card>
        ) : (
          <div className="space-y-6">
            {myTasks.length > 0 && (
              <div>
                <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3 px-1">Assigned to you</h2>
                <div className="overflow-x-auto pb-3 -mx-2 px-2">
                  <div className="flex gap-3 min-w-min" data-testid="board-windows">
                    {WINDOWS.filter((w) => w.tasks.length > 0 || filter === w.key).map((w) => (
                      <div key={w.key} className="shrink-0 w-[300px] rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid={`board-window-${w.key}`}>
                        <div className={`p-3 text-white ${w.header}`}>
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>{w.title}</div>
                            <div className="text-lg font-semibold tabular-nums" style={{ fontFamily: "Outfit, sans-serif" }}>{w.tasks.length}</div>
                          </div>
                        </div>
                        <div className="p-2 space-y-2 max-h-[540px] overflow-y-auto">
                          {w.tasks.length === 0 ? (
                            <p className="text-xs text-slate-400 text-center py-6">Nothing here.</p>
                          ) : w.tasks.map((t) => (
                            <BoardTaskCard key={t.id} task={t} tint={w.tint} token={token} author={author} setAuthor={setAuthor}
                              onToggle={() => toggleStatus(t)} onMarkRead={() => markRead(t.id)} onSubtaskCreated={onSubtaskCreated} canDelegate />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {delegated.length > 0 && (
              <div>
                <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3 px-1 flex items-center gap-2">
                  <ArrowRightLeft size={12} /> Sent by you
                </h2>
                <Accordion type="multiple" className="space-y-3" data-testid="board-delegated">
                  {delegated.map((t) => (
                    <TaskAccordion key={t.id} task={t} token={token} author={author} setAuthor={setAuthor}
                      onToggle={() => toggleStatus(t)} onMarkRead={() => markRead(t.id)} onSubtaskCreated={onSubtaskCreated}
                      onEdit={() => setEditTask(t)} onDelete={() => onDeleteAuthoredTask(t)} />
                  ))}
                </Accordion>
              </div>
            )}
          </div>
        )}
        </>
        )}
      </main>
      <NewTaskDialog open={newTaskOpen} onOpenChange={setNewTaskOpen} token={token} onCreated={onNewTaskCreated} />
      <NewTaskDialog open={!!editTask} onOpenChange={(v) => !v && setEditTask(null)} token={token}
        onCreated={(t) => { onTaskUpdated(t); setEditTask(null); }} editTask={editTask} />
    </div>
  );
}

function BoardTaskCard({ task, tint, token, author, setAuthor, onToggle, onMarkRead, onSubtaskCreated, canDelegate = false }) {
  const done = task.status === "completed";
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [delegateOpen, setDelegateOpen] = useState(false);

  const expand = async () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) {
      try {
        const { data } = await axios.get(`${API}/public/board/${token}/tasks/${task.id}/comments`, { headers: boardHeaders(token) });
        setComments(data); setLoaded(true);
      } catch (err) { toast.error(formatApiError(err)); }
    }
    if (next && task.unread) onMarkRead();
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!text.trim() || !author.trim()) return;
    try {
      const { data } = await axios.post(`${API}/public/board/${token}/tasks/${task.id}/comments`,
        { author: author.trim(), text: text.trim() }, { headers: boardHeaders(token) });
      setComments((prev) => [...prev, data]); setText(""); toast.success("Comment added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <>
    <div className={`p-3 rounded-md border border-slate-200 border-l-4 shadow-sm bg-white ${tint} ${task.unread ? "ring-1 ring-blue-200" : ""}`} data-testid={`board-task-card-${task.id}`}>
      <button type="button" onClick={expand} className="w-full text-left">
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className={`text-sm font-semibold text-slate-900 truncate ${done ? "line-through opacity-60" : ""}`} style={{ fontFamily: "Outfit, sans-serif" }}>
            {task.title}
          </div>
          {task.unread && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-blue-600 text-white uppercase tracking-wider">New</span>
          )}
        </div>
        {task.description && (
          <p className="text-xs text-slate-600 mt-1 line-clamp-2 leading-snug">{task.description}</p>
        )}
        <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-500">
          <span>Due {formatDate(task.deadline).split(",")[0]}</span>
          {task.comment_count > 0 && <span className="inline-flex items-center gap-0.5"><MessageSquare size={9} /> {task.comment_count}</span>}
        </div>
      </button>
      {open && (
        <div className="mt-3 pt-3 border-t border-slate-200 space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant={done ? "outline" : "default"} size="sm" onClick={onToggle} data-testid={`board-toggle-${task.id}`}>
              {done ? <><RotateCcw size={13} className="mr-1.5" /> Reopen</> : <><CheckCircle2 size={13} className="mr-1.5" /> Complete</>}
            </Button>
            {canDelegate && (
              <Button type="button" variant="outline" size="sm" onClick={() => setDelegateOpen(true)} data-testid={`board-delegate-${task.id}`}>
                <UserPlus size={13} className="mr-1.5" /> Delegate
              </Button>
            )}
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-1.5">
              <MessageSquare size={12} /> Comments {comments.length > 0 && <span className="text-slate-400">({comments.length})</span>}
            </div>
            {comments.length === 0 ? (
              <p className="text-[11px] text-slate-500">No comments yet.</p>
            ) : (
              <ul className="space-y-2">
                {comments.map((c) => (
                  <li key={c.id} className="border-l-2 border-slate-200 pl-2">
                    <div className="text-[10px]">
                      <span className="font-medium text-slate-900">{c.author}</span>
                      <span className="text-slate-400 ml-1.5">{formatDate(c.created_at)}</span>
                    </div>
                    <p className="text-xs text-slate-700 mt-0.5">{c.text}</p>
                  </li>
                ))}
              </ul>
            )}
            <form onSubmit={addComment} className="mt-2 space-y-1.5">
              <Input placeholder="Your name" value={author} onChange={(e) => setAuthor(e.target.value)} data-testid={`board-comment-author-${task.id}`} className="h-8 text-xs" />
              <Textarea rows={2} placeholder="Add a comment…" value={text} onChange={(e) => setText(e.target.value)} data-testid={`board-comment-input-${task.id}`} className="text-xs" />
              <div className="flex justify-end">
                <Button type="submit" size="sm" disabled={!text.trim() || !author.trim()} data-testid={`board-comment-submit-${task.id}`}>
                  <Send size={12} className="mr-1" /> Post
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    <DelegateDialog open={delegateOpen} onOpenChange={setDelegateOpen} parentTask={task} token={token} onCreated={onSubtaskCreated} />
    </>
  );
}

const BOARD_TODO_COLORS = [
  "bg-rose-50 border-rose-200", "bg-amber-50 border-amber-200", "bg-emerald-50 border-emerald-200",
  "bg-sky-50 border-sky-200", "bg-violet-50 border-violet-200", "bg-fuchsia-50 border-fuchsia-200",
];
function todoColor(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return BOARD_TODO_COLORS[h % BOARD_TODO_COLORS.length];
}

function BoardTodo({ token }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [editing, setEditing] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/board/${token}/todos`, { headers: boardHeaders(token) });
        setItems(data);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
     
  }, [token]);

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await axios.post(`${API}/public/board/${token}/todos`, { text: text.trim() }, { headers: boardHeaders(token) });
      setItems((prev) => [...prev, data]); setText("");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const toggle = async (t) => {
    try {
      const { data } = await axios.patch(`${API}/public/board/${token}/todos/${t.id}`, { done: !t.done }, { headers: boardHeaders(token) });
      setItems((prev) => prev.map((x) => (x.id === t.id ? data : x)));
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const save = async (t) => {
    const nt = editing[t.id]?.trim();
    if (!nt || nt === t.text) { setEditing((p) => ({ ...p, [t.id]: undefined })); return; }
    try {
      const { data } = await axios.patch(`${API}/public/board/${token}/todos/${t.id}`, { text: nt }, { headers: boardHeaders(token) });
      setItems((prev) => prev.map((x) => (x.id === t.id ? data : x)));
      setEditing((p) => ({ ...p, [t.id]: undefined })); toast.success("Updated");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (t) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/public/board/${token}/todos/${t.id}`, { headers: boardHeaders(token) });
      setItems((prev) => prev.filter((x) => x.id !== t.id));
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const open = items.filter((i) => !i.done);
  const done = items.filter((i) => i.done);

  return (
    <div>
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 shadow-sm text-xs text-slate-600 mb-3">
        <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500" /> Private to you — the admin cannot see this
      </div>
      <Card className="rounded-xl border-slate-200 shadow-sm p-3 mb-4 bg-white">
        <form onSubmit={add} className="flex gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a personal to-do…" className="flex-1 h-10" data-testid="board-todo-input" />
          <Button type="submit" disabled={!text.trim()} data-testid="board-todo-add">Add</Button>
        </form>
      </Card>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> :
       items.length === 0 ? (
        <Card className="p-8 rounded-xl border-dashed border-slate-300 shadow-none text-center bg-white/60">
          <p className="text-sm text-slate-500">Nothing here yet — jot down anything you want to remember.</p>
        </Card>
       ) : (
        <div className="space-y-4" data-testid="board-todo-list">
          {open.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold px-1">Open ({open.length})</div>
              {open.map((t) => (
                <BoardTodoRow key={t.id} t={t} tint={todoColor(t.id)}
                  editing={editing[t.id] !== undefined} editValue={editing[t.id] ?? t.text}
                  onEdit={() => setEditing((p) => ({ ...p, [t.id]: t.text }))}
                  onEditChange={(v) => setEditing((p) => ({ ...p, [t.id]: v }))}
                  onSave={() => save(t)} onCancel={() => setEditing((p) => ({ ...p, [t.id]: undefined }))}
                  onToggle={() => toggle(t)} onRemove={() => remove(t)} />
              ))}
            </div>
          )}
          {done.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold px-1">Completed ({done.length})</div>
              {done.map((t) => (
                <BoardTodoRow key={t.id} t={t} tint="bg-slate-100 border-slate-200"
                  editing={editing[t.id] !== undefined} editValue={editing[t.id] ?? t.text}
                  onEdit={() => setEditing((p) => ({ ...p, [t.id]: t.text }))}
                  onEditChange={(v) => setEditing((p) => ({ ...p, [t.id]: v }))}
                  onSave={() => save(t)} onCancel={() => setEditing((p) => ({ ...p, [t.id]: undefined }))}
                  onToggle={() => toggle(t)} onRemove={() => remove(t)} />
              ))}
            </div>
          )}
        </div>
       )}
    </div>
  );
}

function BoardTodoRow({ t, tint, editing, editValue, onEdit, onEditChange, onSave, onCancel, onToggle, onRemove }) {
  return (
    <div className={`group p-3 rounded-lg border-2 shadow-sm transition-all ${tint} ${t.done ? "opacity-70" : ""}`} data-testid={`board-todo-row-${t.id}`}>
      <div className="flex items-start gap-3">
        <input type="checkbox" checked={t.done} onChange={onToggle} className="mt-1" data-testid={`board-todo-check-${t.id}`} />
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex gap-2">
              <Input value={editValue} onChange={(e) => onEditChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") onSave(); if (e.key === "Escape") onCancel(); }}
                autoFocus className="h-8 text-sm" data-testid={`board-todo-edit-${t.id}`} />
              <Button size="sm" onClick={onSave} data-testid={`board-todo-save-${t.id}`}>Save</Button>
            </div>
          ) : (
            <button type="button" onClick={onEdit} className={`text-left text-sm text-slate-900 w-full ${t.done ? "line-through" : ""}`} data-testid={`board-todo-text-${t.id}`}>
              {t.text}
            </button>
          )}
        </div>
        <button type="button" onClick={onRemove} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 transition-opacity" data-testid={`board-todo-delete-${t.id}`} aria-label="Delete">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
        </button>
      </div>
    </div>
  );
}

function StatChip({ label, value, icon, accent }) {
  return (
    <div className="flex items-center gap-2 p-2 rounded-md border border-slate-200 bg-white">
      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md ${accent}`}>{icon}</span>
      <div>
        <div className="text-lg font-semibold text-slate-900 leading-none" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{label}</div>
      </div>
    </div>
  );
}

function TaskAccordion({ task, token, author, setAuthor, onToggle, onMarkRead, onSubtaskCreated, onEdit, onDelete, canDelegate = false }) {
  const done = task.status === "completed";
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [delegateOpen, setDelegateOpen] = useState(false);

  const loadComments = async () => {
    try {
      const { data } = await axios.get(`${API}/public/board/${token}/tasks/${task.id}/comments`, { headers: boardHeaders(token) });
      setComments(data); setLoaded(true);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const addComment = async (e) => {
    e.preventDefault();
    if (!text.trim() || !author.trim()) return;
    try {
      const { data } = await axios.post(`${API}/public/board/${token}/tasks/${task.id}/comments`, { author: author.trim(), text: text.trim() }, { headers: boardHeaders(token) });
      setComments((prev) => [...prev, data]); setText(""); toast.success("Comment added");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const copySubShare = async () => {
    if (!task.sub_share_token) return;
    const url = `${window.location.origin}/board/${task.sub_share_token}`;
    try { await navigator.clipboard.writeText(url); toast.success(`Copied ${task.assignee_name}'s board link`); }
    catch { toast.error("Could not copy"); }
  };
  const onExpand = () => { if (!loaded) loadComments(); if (task.unread) onMarkRead(); };

  return (
    <>
      <AccordionItem value={task.id}
        className={`border rounded-md bg-white shadow-sm ${task.unread ? "border-blue-300 ring-1 ring-blue-100" : "border-slate-200"} data-[state=open]:border-slate-300 ${done ? "opacity-80" : ""}`}
        data-testid={`board-task-${task.id}`}>
        <AccordionTrigger className="px-5 py-4 hover:no-underline" onClick={onExpand}>
          <div className="flex-1 flex items-start gap-3 min-w-0 text-left">
            <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${statusDotClass(task.color)}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className={`text-base font-semibold text-slate-900 truncate ${done ? "line-through" : ""}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                  {task.title}
                </div>
                {task.unread && (
                  <Badge className="bg-blue-600 hover:bg-blue-600 text-white text-[10px] px-1.5 py-0.5 h-auto" data-testid={`board-task-unread-${task.id}`}>
                    <span className="w-1 h-1 rounded-full bg-white mr-1 animate-pulse" /> New
                  </Badge>
                )}
                {task.delegated_by_me && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 h-auto border-slate-300">Delegated → {task.assignee_name}</Badge>
                )}
                {task.author_view && !task.delegated_by_me && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 h-auto border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50">Created by you → {task.assignee_name}</Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><CalIcon size={11} /> Assigned {formatDate(task.assigned_at)}</span>
                <span className="inline-flex items-center gap-1"><CalIcon size={11} /> Due {formatDate(task.deadline)}</span>
              </div>
            </div>
            <Badge variant="outline" className={`border ${statusBadgeClasses(task.color)} shrink-0`}>{statusLabel(task.color, task.status)}</Badge>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-5 pb-5 pt-0 space-y-4">
          {task.description && <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{task.description}</p>}
          <div className="flex flex-wrap gap-2">
            {!task.delegated_by_me && (
              <Button type="button" variant={done ? "outline" : "default"} size="sm"
                onClick={(e) => { e.stopPropagation(); onToggle(); }} data-testid={`board-toggle-${task.id}`}>
                {done ? <><RotateCcw size={13} className="mr-1.5" /> Mark as in progress</> : <><CheckCircle2 size={13} className="mr-1.5" /> Mark as completed</>}
              </Button>
            )}
            {canDelegate && !task.delegated_by_me && (
              <Button type="button" variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setDelegateOpen(true); }} data-testid={`board-delegate-${task.id}`}>
                <UserPlus size={13} className="mr-1.5" /> Delegate
              </Button>
            )}
            {task.delegated_by_me && task.sub_share_token && (
              <Button type="button" variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); copySubShare(); }} data-testid={`board-copy-sub-${task.id}`}>
                <Copy size={13} className="mr-1.5" /> Copy {task.assignee_name}'s link
              </Button>
            )}
            {task.authored_by_me && onEdit && (
              <Button type="button" variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onEdit(); }} data-testid={`board-edit-${task.id}`}>
                <Pencil size={13} className="mr-1.5" /> Edit
              </Button>
            )}
            {task.authored_by_me && onDelete && (
              <Button type="button" variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="text-rose-700 hover:text-rose-800 border-rose-200 hover:bg-rose-50" data-testid={`board-delete-${task.id}`}>
                <Trash2 size={13} className="mr-1.5" /> Delete
              </Button>
            )}
          </div>
          <div className="pt-2">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <MessageSquare size={14} /> Comments {comments.length > 0 && <span className="text-slate-400">({comments.length})</span>}
            </div>
            {comments.length === 0 ? (
              <p className="text-xs text-slate-500">No comments yet.</p>
            ) : (
              <ul className="space-y-3">
                {comments.map((c) => (
                  <li key={c.id} className="border-l-2 border-slate-200 pl-3">
                    <div className="text-xs">
                      <span className="font-medium text-slate-900">{c.author}</span>
                      <span className="text-slate-400 ml-2">{formatDate(c.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-700 mt-0.5 leading-relaxed">{c.text}</p>
                  </li>
                ))}
              </ul>
            )}
            <form onSubmit={addComment} className="mt-3 space-y-2">
              <Input placeholder="Your name" value={author} onChange={(e) => setAuthor(e.target.value)} data-testid={`board-comment-author-${task.id}`} />
              <Textarea rows={2} placeholder="Add a comment…" value={text} onChange={(e) => setText(e.target.value)} data-testid={`board-comment-input-${task.id}`} />
              <div className="flex justify-end">
                <Button type="submit" size="sm" disabled={!text.trim() || !author.trim()} data-testid={`board-comment-submit-${task.id}`}>
                  <Send size={13} className="mr-1.5" /> Post
                </Button>
              </div>
            </form>
          </div>
        </AccordionContent>
      </AccordionItem>

      <DelegateDialog open={delegateOpen} onOpenChange={setDelegateOpen} parentTask={task} token={token} onCreated={onSubtaskCreated} />
    </>
  );
}

function DelegateDialog({ open, onOpenChange, parentTask, token, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [deadlineDate, setDeadlineDate] = useState();
  const [deadlineTime, setDeadlineTime] = useState("17:00");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && parentTask) {
      setTitle(`Sub-task: ${parentTask.title}`);
      setDescription(""); setName(""); setEmail("");
      const d = new Date(parentTask.deadline);
      setDeadlineDate(d);
      setDeadlineTime(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
    }
  }, [open, parentTask]);

  const submit = async (e) => {
    e.preventDefault();
    if (!deadlineDate) return toast.error("Pick a deadline date");
    const [hh, mm] = deadlineTime.split(":");
    const dt = new Date(deadlineDate);
    dt.setHours(parseInt(hh, 10) || 17, parseInt(mm, 10) || 0, 0, 0);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/board/${token}/tasks/${parentTask.id}/subtasks`,
        { title, description, assignee_name: name, assignee_email: email, deadline: dt.toISOString() },
        { headers: boardHeaders(token) });
      onCreated(data);
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="delegate-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Delegate to a teammate</DialogTitle>
          <p className="text-xs text-slate-500">Only you, the admin, and the person you pick will see this sub-task.</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Title</Label><Input required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="delegate-title-input" /></div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="delegate-description-input" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Teammate name</Label><Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="delegate-name-input" /></div>
            <div className="space-y-1.5"><Label>Teammate email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="delegate-email-input" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Deadline date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start font-normal" type="button" data-testid="delegate-deadline-date-btn">
                    <CalendarIcon size={14} className="mr-2" />{deadlineDate ? formatDate(deadlineDate).split(",")[0] : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0">
                  <Calendar mode="single" selected={deadlineDate} onSelect={setDeadlineDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1.5"><Label>Deadline time</Label><Input type="time" value={deadlineTime} onChange={(e) => setDeadlineTime(e.target.value)} data-testid="delegate-deadline-time-input" /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="delegate-submit-btn">{busy ? "Delegating…" : "Delegate"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function NewTaskDialog({ open, onOpenChange, token, onCreated, editTask }) {
  const isEdit = !!editTask;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [deadlineDate, setDeadlineDate] = useState();
  const [deadlineTime, setDeadlineTime] = useState("17:00");
  const [priority, setPriority] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [busy, setBusy] = useState(false);
  const [known, setKnown] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (isEdit && editTask) {
      setTitle(editTask.title || "");
      setDescription(editTask.description || "");
      setName(editTask.assignee_name || "");
      setEmail(editTask.assignee_email || "");
      const d = new Date(editTask.deadline);
      setDeadlineDate(d);
      setDeadlineTime(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
      setPriority(editTask.priority || "");
      setTagsText((editTask.tags || []).join(", "));
    } else {
      setTitle(""); setDescription(""); setName(""); setEmail("");
      setDeadlineDate(undefined); setDeadlineTime("17:00");
      setPriority(""); setTagsText("");
    }
    // Load known assignees for the dropdown
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/board/${token}/assignees`, { headers: boardHeaders(token) });
        setKnown(data || []);
      } catch {}
    })();
     
  }, [open, isEdit, editTask, token]);

  const pickKnown = (e) => {
    const v = e.target.value;
    if (!v) return;
    const person = known.find((k) => k.email === v);
    if (person) {
      setEmail(person.email);
      setName(person.name);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!deadlineDate) return toast.error("Pick a deadline date");
    if (!name.trim() || !email.trim()) return toast.error("Assignee name and email are required");
    const [hh, mm] = deadlineTime.split(":");
    const dt = new Date(deadlineDate);
    dt.setHours(parseInt(hh, 10) || 17, parseInt(mm, 10) || 0, 0, 0);
    const tags = tagsText.split(",").map((s) => s.trim()).filter(Boolean);
    const payload = {
      title, description,
      assignee_name: name, assignee_email: email,
      deadline: dt.toISOString(),
      priority: priority || null,
      tags,
    };
    setBusy(true);
    try {
      if (isEdit) {
        const { data } = await axios.patch(`${API}/public/board/${token}/tasks/${editTask.id}`,
          payload, { headers: boardHeaders(token) });
        onCreated(data);
      } else {
        const { data } = await axios.post(`${API}/public/board/${token}/tasks/new`,
          payload, { headers: boardHeaders(token) });
        onCreated(data);
      }
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-task-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>{isEdit ? "Edit task" : "Create a new task"}</DialogTitle>
          <p className="text-xs text-slate-500">{isEdit
            ? "Update this task you created. Admins can also see and edit it."
            : "Assign to anyone — a known teammate from the list, or enter a new email."}</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-task-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="new-task-description-input" />
          </div>
          {known.length > 0 && (
            <div className="space-y-1.5">
              <Label>Pick from known people (optional)</Label>
              <select onChange={pickKnown} defaultValue="" data-testid="new-task-known-select"
                className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">— Select a teammate —</option>
                {known.map((k) => (
                  <option key={k.email} value={k.email}>{k.name} ({k.email}){k.role ? ` · ${k.role.replace("_", " ")}` : ""}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Assignee name</Label><Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="new-task-name-input" /></div>
            <div className="space-y-1.5"><Label>Assignee email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="new-task-email-input" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Deadline date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start font-normal" type="button" data-testid="new-task-deadline-date-btn">
                    <CalendarIcon size={14} className="mr-2" />{deadlineDate ? formatDate(deadlineDate).split(",")[0] : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0">
                  <Calendar mode="single" selected={deadlineDate} onSelect={setDeadlineDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1.5"><Label>Deadline time</Label><Input type="time" value={deadlineTime} onChange={(e) => setDeadlineTime(e.target.value)} data-testid="new-task-deadline-time-input" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Flag size={12} /> Priority</Label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="new-task-priority-select"
                className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">— None —</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Tags (comma-separated)</Label>
              <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="e.g. sales, urgent" data-testid="new-task-tags-input" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="new-task-submit-btn">
              {busy ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save changes" : "Create task")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
